import { IShiftFormCustomField } from 'modals/ShiftModal/src/components/ShiftCustomFields/ShiftCustomFields.types';
import { SCHEDULING_PUBLISHED_SHIFT_DETAILS } from 'constants/endpoints';
import { METHOD_PATCH } from 'requests';
import { DateTimeType } from 'requests/POST_createOrders.types';
import { ApiRequestConfigType } from 'requests/index.types';
import { OrderStatusEnum } from 'types/graphql';

export type UpdateOrderDetailsParams = {
  description?: string;
  shift_id: number;
  notification_method?: string;
  reaccept_or_decline_threshold?: number;
  start_date_time?: DateTimeType;
  end_date_time?: DateTimeType;
  unpaid_break_time?: string;
  isOnlyNotesUpdated?: boolean;
  custom_fields?: IShiftFormCustomField[];
  allow_overtime?: boolean;
  auto_rebid?: boolean;
  zero_hours_worked?: boolean;
  reason_category?: string;
  reason_message?: string;
  status?: OrderStatusEnum;
  acceptedByAgency?: boolean;
  sub_facility_address_id?: string;
  position_id?: string;
  jane_id?: string | null;
  orders_skills_attributes?: OrdersSkillsAttribute[];
};

export type OrdersSkillsAttribute = { skill_id: number } | { id: number; _destroy: true };

type UpdateOrderRequestBodyType = {
  order: {
    description?: string;
    notification_method?: string;
    reaccept_or_decline_threshold?: number;
    start_date_time?: DateTimeType;
    end_date_time?: DateTimeType;
    unpaid_break_time?: string;
    custom_fields?: IShiftFormCustomField[];
    allow_overtime?: boolean;
    auto_rebid?: boolean;
    sub_facility_address_id?: string;
    no_show?: boolean;
    reason?: { reason_category: string; reason_message?: string };
    position_id?: string;
    jane_id?: string | null;
    orders_skills_attributes?: OrdersSkillsAttribute[];
  };
};

export const API_PATCH_updateOrderDetails = (
  params: UpdateOrderDetailsParams,
): ApiRequestConfigType => {
  const {
    description,
    shift_id,
    notification_method,
    reaccept_or_decline_threshold,
    start_date_time,
    end_date_time,
    unpaid_break_time,
    isOnlyNotesUpdated = false,
    custom_fields,
    allow_overtime,
    auto_rebid,
    sub_facility_address_id,
    zero_hours_worked,
    reason_category,
    reason_message,
    status,
    position_id,
    jane_id,
    orders_skills_attributes,
  } = params;

  const isShowEditReason =
    status === OrderStatusEnum.in_progress || status === OrderStatusEnum.completed;

  const body: UpdateOrderRequestBodyType = {
    order: {},
  };

  // Only include fields that are explicitly provided
  if (description !== undefined) {
    body.order.description = description;
  }

  if (notification_method !== undefined) {
    body.order.notification_method = notification_method;
  }

  if (reaccept_or_decline_threshold !== undefined) {
    body.order.reaccept_or_decline_threshold = reaccept_or_decline_threshold;
  }

  if (start_date_time !== undefined && !zero_hours_worked) {
    body.order.start_date_time = start_date_time;
  }

  if (end_date_time !== undefined && !zero_hours_worked) {
    body.order.end_date_time = end_date_time;
  }

  if (unpaid_break_time !== undefined) {
    body.order.unpaid_break_time = unpaid_break_time;
  }

  if (custom_fields !== undefined) {
    body.order.custom_fields = custom_fields;
  }

  if (allow_overtime !== undefined) {
    body.order.allow_overtime = allow_overtime;
  }

  if (auto_rebid !== undefined) {
    body.order.auto_rebid = auto_rebid;
  }

  if (sub_facility_address_id !== undefined) {
    body.order.sub_facility_address_id = sub_facility_address_id;
  }

  if (zero_hours_worked !== undefined) {
    body.order.no_show = zero_hours_worked;
  }

  if (position_id !== undefined) {
    body.order.position_id = position_id;
  }

  if (jane_id !== undefined) {
    body.order.jane_id = jane_id;
  }

  if (orders_skills_attributes?.length) {
    body.order.orders_skills_attributes = orders_skills_attributes;
  }

  if (isShowEditReason && reason_category) {
    body.order.reason = {
      reason_category,
      ...(reason_message && { reason_message }),
    };
  }

  if (isOnlyNotesUpdated) {
    delete body.order.reaccept_or_decline_threshold;
    delete body.order.notification_method;
    delete body.order.start_date_time;
    delete body.order.end_date_time;
    delete body.order.unpaid_break_time;
  } else if (!notification_method) {
    delete body.order.reaccept_or_decline_threshold;
  } else if (notification_method !== 'reaccept_or_decline') {
    delete body.order.reaccept_or_decline_threshold;
  }

  return {
    __typename: 'LegacyAPI',
    url: SCHEDULING_PUBLISHED_SHIFT_DETAILS(shift_id.toString()),
    method: METHOD_PATCH,
    body,
  };
};
